import { NoWrap, numberFormat, returnObject } from "../../../../utils/helpers";

type TAgentDepositsPerformanceProps = {
  dateFilterKey?: string;
  status?: string;
  noWrapColumns?: string[];
  numberColumns?: string[];
};

const useAgentDepositsPerformance = ({
  dateFilterKey,
  noWrapColumns = [],
  numberColumns = [],
}: TAgentDepositsPerformanceProps) => {
  const tableColumns = () => {
    const filterColumns = [
      {
        id: "full_name",
        header: <NoWrap>Agent Name</NoWrap>,
        filter: { type: "text" },
      },
      {
        id: "bank_id",
        header: <NoWrap>Bank ID</NoWrap>,
        filter: { type: "text" },
      },
      {
        id: "deposited_day",
        header: <NoWrap>Deposit Date</NoWrap>,
        filter: { type: "date" },
      },
      {
        id: "expected_deposit",
        header: <NoWrap>Expected Amount</NoWrap>,
        filter: { type: "number" },
      },
      {
        id: "total_deposited",
        header: <NoWrap>Deposited Amount</NoWrap>,
        filter: { type: "number" },
      },
      {
        id: "gl_status",
        header: <NoWrap>Status</NoWrap>,
        filter: { type: "text" },
      },
      {
        id: "deposit_partner",
        header: <NoWrap>Partner</NoWrap>,
        filter: { type: "text" },
      },
    ];

    return [...filterColumns];
  };

  const tableData = (data: any) => {
    return data?.map((item: any) => {
      const variance =
        (item.total_deposited || 0) - (item.expected_deposit || 0);
      const variancePercentage =
        item.expected_deposit > 0
          ? ((variance / item.expected_deposit) * 100).toFixed(1)
          : "0.0";

      return {
        ...item,
        [dateFilterKey || "record_date"]: (
          <NoWrap>{item[dateFilterKey || "record_date"]}</NoWrap>
        ),
        full_name: (
          <span className="font-medium text-gray-900">
            <NoWrap>{item.full_name || "Unknown"}</NoWrap>
          </span>
        ),
        bank_id: <NoWrap>{item.bank_id || "N/A"}</NoWrap>,
        deposited_day: <NoWrap>{item.deposited_day}</NoWrap>,
        expected_deposit: (
          <span className="font-medium text-blue-600">
            {numberFormat(item.expected_deposit)}
          </span>
        ),
        total_deposited: (
          <span className="font-medium text-green-600">
            {numberFormat(item.total_deposited)}
          </span>
        ),
        variance: (
          <span
            className={`font-medium ${
              variance > 0
                ? "text-green-600"
                : variance < 0
                  ? "text-red-600"
                  : "text-gray-600"
            }`}
          >
            {variance > 0 ? "+" : ""}
            {numberFormat(variance)}
          </span>
        ),
        variance_percentage: (
          <span
            className={`px-2 py-1 rounded text-xs font-medium ${
              parseFloat(variancePercentage) > 10
                ? "bg-green-100 text-green-800"
                : parseFloat(variancePercentage) < -10
                  ? "bg-red-100 text-red-800"
                  : "bg-yellow-100 text-yellow-800"
            }`}
          >
            {variancePercentage}%
          </span>
        ),
        gl_status: (
          <span
            className={`px-2 py-1 rounded text-xs font-medium ${
              item.gl_status === "success"
                ? "bg-green-100 text-green-800"
                : item.gl_status?.includes("Failed")
                  ? "bg-red-100 text-red-800"
                  : "bg-yellow-100 text-yellow-800"
            }`}
          >
            {item.gl_status}
          </span>
        ),
        deposit_partner: (
          <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs">
            {item.deposit_partner}
          </span>
        ),
        is_reconciled: (
          <span
            className={`px-2 py-1 rounded text-xs font-medium ${
              item.is_reconciled
                ? "bg-green-100 text-green-800"
                : "bg-red-100 text-red-800"
            }`}
          >
            {item.is_reconciled ? "Reconciled" : "Pending"}
          </span>
        ),
        performance_rating: (
          <span
            className={`px-2 py-1 rounded text-xs font-medium ${
              parseFloat(variancePercentage) >= 0 &&
              item.gl_status === "success"
                ? "bg-green-100 text-green-800"
                : parseFloat(variancePercentage) < -20 ||
                    item.gl_status?.includes("Failed")
                  ? "bg-red-100 text-red-800"
                  : "bg-orange-100 text-orange-800"
            }`}
          >
            {parseFloat(variancePercentage) >= 0 && item.gl_status === "success"
              ? "Excellent"
              : parseFloat(variancePercentage) < -20 ||
                  item.gl_status?.includes("Failed")
                ? "Poor"
                : "Good"}
          </span>
        ),
        ...returnObject(
          noWrapColumns.length > 0,
          noWrapColumns.reduce((obj, column) => {
            if (item[column] !== undefined) {
              obj[column] = <NoWrap>{item[column]}</NoWrap>;
            }
            return obj;
          }, {}),
        ),
        ...returnObject(
          numberColumns.length > 0,
          numberColumns.reduce((obj, column) => {
            if (item[column] !== undefined) {
              obj[column] = numberFormat(item[column]);
            }
            return obj;
          }, {}),
        ),
      };
    });
  };

  const initialFilters = {
    full_name: "",
    bank_id: "",
    deposited_day: "",
    expected_deposit: "",
    total_deposited: "",
    gl_status: "",
    deposit_partner: "",
  };

  const getPerformanceStats = (data: any) => {
    const deposits = data?.results || [];

    const totalDeposits = deposits.length;
    const successfulDeposits = deposits.filter(
      (item: any) => item.gl_status === "success",
    ).length;

    const totalExpected = deposits.reduce(
      (sum: number, item: any) =>
        sum + (parseFloat(item.expected_deposit) || 0),
      0,
    );

    const totalDeposited = deposits.reduce(
      (sum: number, item: any) => sum + (parseFloat(item.total_deposited) || 0),
      0,
    );

    const totalVariance = totalDeposited - totalExpected;
    const successRate =
      totalDeposits > 0
        ? ((successfulDeposits / totalDeposits) * 100).toFixed(1)
        : "0.0";
    const averageDepositSize =
      totalDeposits > 0 ? (totalDeposited / totalDeposits).toFixed(0) : "0";

    const reconciledDeposits = deposits.filter(
      (item: any) => item.is_reconciled,
    ).length;
    const reconciliationRate =
      totalDeposits > 0
        ? ((reconciledDeposits / totalDeposits) * 100).toFixed(1)
        : "0.0";

    return {
      totalDeposits,
      successfulDeposits,
      totalExpected,
      totalDeposited,
      totalVariance,
      successRate,
      averageDepositSize,
      reconciliationRate,
      variancePercentage:
        totalExpected > 0
          ? ((totalVariance / totalExpected) * 100).toFixed(1)
          : "0.0",
    };
  };

  const getAgentPerformance = (data: any) => {
    const deposits = data?.results || [];

    // Group by agent
    const agentGroups = deposits.reduce((acc: any, item: any) => {
      const agent = item.full_name || "Unknown";
      if (!acc[agent]) {
        acc[agent] = {
          deposit_count: 0,
          total_expected: 0,
          total_deposited: 0,
          successful_deposits: 0,
          failed_deposits: 0,
          reconciled_deposits: 0,
          // user_id: item.user_id,
        };
      }

      acc[agent].deposit_count++;
      acc[agent].total_expected += parseFloat(item.expected_deposit) || 0;
      acc[agent].total_deposited += parseFloat(item.total_deposited) || 0;

      if (item.gl_status === "success") {
        acc[agent].successful_deposits++;
      } else {
        acc[agent].failed_deposits++;
      }

      if (item.is_reconciled) {
        acc[agent].reconciled_deposits++;
      }

      return acc;
    }, {});

    return Object.entries(agentGroups)
      .map(([agent, stats]: [string, any]) => {
        const variance = stats.total_deposited - stats.total_expected;
        const successRate =
          stats.deposit_count > 0
            ? ((stats.successful_deposits / stats.deposit_count) * 100).toFixed(
                1,
              )
            : "0.0";
        const reconciliationRate =
          stats.deposit_count > 0
            ? ((stats.reconciled_deposits / stats.deposit_count) * 100).toFixed(
                1,
              )
            : "0.0";

        return {
          agent_name: agent,
          // user_id: stats.user_id,
          deposit_count: stats.deposit_count,
          total_expected: stats.total_expected,
          total_deposited: stats.total_deposited,
          variance,
          variance_percentage:
            stats.total_expected > 0
              ? ((variance / stats.total_expected) * 100).toFixed(1)
              : "0.0",
          success_rate: successRate,
          reconciliation_rate: reconciliationRate,
          average_deposit:
            stats.deposit_count > 0
              ? (stats.total_deposited / stats.deposit_count).toFixed(0)
              : "0",
          performance_grade:
            parseFloat(successRate) >= 90 && variance >= 0
              ? "A"
              : parseFloat(successRate) >= 75 &&
                  variance >= -stats.total_expected * 0.1
                ? "B"
                : parseFloat(successRate) >= 60
                  ? "C"
                  : "D",
        };
      })
      .sort((a: any, b: any) => b.total_deposited - a.total_deposited);
  };

  const getPerformanceBuckets = (data: any) => {
    const deposits = data?.results || [];

    const buckets = {
      excellent: { count: 0, amount: 0, label: "Over Target (>100%)" },
      good: { count: 0, amount: 0, label: "On Target (90-100%)" },
      fair: { count: 0, amount: 0, label: "Below Target (50-89%)" },
      poor: { count: 0, amount: 0, label: "Poor (<50%)" },
    };

    deposits.forEach((item: any) => {
      const expected = parseFloat(item.expected_deposit) || 0;
      const deposited = parseFloat(item.total_deposited) || 0;
      const percentage = expected > 0 ? (deposited / expected) * 100 : 0;

      if (percentage > 100) {
        buckets.excellent.count++;
        buckets.excellent.amount += deposited;
      } else if (percentage >= 90) {
        buckets.good.count++;
        buckets.good.amount += deposited;
      } else if (percentage >= 50) {
        buckets.fair.count++;
        buckets.fair.amount += deposited;
      } else {
        buckets.poor.count++;
        buckets.poor.amount += deposited;
      }
    });

    const totalCount = deposits.length;
    return Object.entries(buckets).map(([key, bucket]) => ({
      category: key,
      label: bucket.label,
      count: bucket.count,
      amount: bucket.amount,
      percentage:
        totalCount > 0 ? ((bucket.count / totalCount) * 100).toFixed(1) : "0.0",
    }));
  };

  const getTopPerformers = (data: any, limit: number = 10) => {
    const agentPerformance = getAgentPerformance(data);

    return agentPerformance
      .filter((agent: any) => agent.deposit_count > 0)
      .sort((a: any, b: any) => {
        // Sort by performance grade first, then by total deposited
        const gradeOrder = { A: 4, B: 3, C: 2, D: 1 };
        const aGrade =
          gradeOrder[a.performance_grade as keyof typeof gradeOrder] || 0;
        const bGrade =
          gradeOrder[b.performance_grade as keyof typeof gradeOrder] || 0;

        if (aGrade !== bGrade) {
          return bGrade - aGrade;
        }
        return b.total_deposited - a.total_deposited;
      })
      .slice(0, limit)
      .map((agent: any, index: number) => ({
        rank: index + 1,
        ...agent,
        efficiency_score: (
          parseFloat(agent.success_rate) * 0.4 +
          parseFloat(agent.reconciliation_rate) * 0.3 +
          Math.min(parseFloat(agent.variance_percentage) + 100, 200) * 0.3
        ).toFixed(1),
      }));
  };

  const getUnderperformers = (data: any, limit: number = 10) => {
    const agentPerformance = getAgentPerformance(data);

    return agentPerformance
      .filter(
        (agent: any) =>
          agent.deposit_count > 0 &&
          (parseFloat(agent.success_rate) < 80 || agent.variance < 0),
      )
      .sort((a: any, b: any) => {
        // Sort by worst performance first
        const aVariance = parseFloat(a.variance_percentage);
        const bVariance = parseFloat(b.variance_percentage);
        return aVariance - bVariance;
      })
      .slice(0, limit)
      .map((agent: any, index: number) => ({
        rank: index + 1,
        ...agent,
        improvement_needed:
          parseFloat(agent.success_rate) < 60
            ? "Critical"
            : parseFloat(agent.variance_percentage) < -20
              ? "High"
              : "Medium",
      }));
  };

  const tableTitle = "Agent Deposit Performance Report";
  return {
    tableColumns,
    tableData,
    tableTitle,
    initialFilters,
    getPerformanceStats,
    getAgentPerformance,
    getPerformanceBuckets,
    getTopPerformers,
    getUnderperformers,
  };
};

export default useAgentDepositsPerformance;
